import React, { PureComponent } from 'react';
import ReactDataSheet from 'react-datasheet';
import { Sheet } from './sheet';
import { DataCell } from './datacell';
import { DefaultRowRenderer as Row, DefaultValueViewer as ValueViewer } from './default_components';
import { CustomDataEditor } from './dataeditor';

import {
  TAB_KEY,
  ENTER_KEY,
  DELETE_KEY,
  ESCAPE_KEY,
  BACKSPACE_KEY,
  LEFT_KEY,
  UP_KEY,
  DOWN_KEY,
  RIGHT_KEY,
} from './keys';
import { GridElement, Value, HandleCopyFunction } from './types';
import DataSheetProps = ReactDataSheet.DataSheetProps;
import CellAdditionsArgs = ReactDataSheet.CellAdditionsArgs;
import CellsChangedArgs = ReactDataSheet.CellsChangedArgs;
import Location = ReactDataSheet.Location;

export type CustomDataSheetProps<T extends ReactDataSheet.Cell<T, V>, V = string> = Omit<DataSheetProps<T, V>, 'handleCopy' | 'onContextMenu'> & {
  readonly?: boolean,
  handleCopy: HandleCopyFunction<T, V>;
  onContextMenu?: (e: React.MouseEvent<HTMLElement>, cell: T, row: number, col: number) => void;
};
type Props = CustomDataSheetProps<GridElement, Value>;

interface State {
  start: Location;
  end: Location;
  selecting: boolean;
  forceEdit: boolean;
  editing: Location;
  clear: Location;
}

// 以下のコードは、上記のPropsとStateインターフェースを使用して、DataSheetクラスを書き換えます。

const isEmpty = (obj: Location) => Object.keys(obj).length === 0 || (obj.i === undefined && obj.j === undefined);
const defaultLocation: () => Location = () => ({ i: undefined, j: undefined });

const range = (start: number, end: number) => {
  const array = [];
  const inc = end - start > 0;
  for (let i = start; inc ? i <= end : i >= end; inc ? i++ : i--) {
    if (inc) {
      array.push(i);
    } else {
      array.unshift(i);
    }
  }
  return array;
};

const defaultParsePaste = (str: string) => {
  return str.split(/\r\n|\n|\r/).map((row) => row.split('\t'));
};

export default class DataSheet extends PureComponent<Props, State> {
  private defaultState: State;

  private dgDom: HTMLSpanElement;

  constructor(props: Props) {
    super(props);
    this.onMouseDown = this.onMouseDown.bind(this);
    this.onMouseUp = this.onMouseUp.bind(this);
    this.onMouseOver = this.onMouseOver.bind(this);
    this.onDoubleClick = this.onDoubleClick.bind(this);
    this.onContextMenu = this.onContextMenu.bind(this);
    this.handleNavigate = this.handleNavigate.bind(this);
    this.handleKey = this.handleKey.bind(this).bind(this);
    this.handleCut = this.handleCut.bind(this);
    this.handleCopy = this.handleCopy.bind(this);
    this.handlePaste = this.handlePaste.bind(this);
    this.pageClick = this.pageClick.bind(this);
    this.onChange = this.onChange.bind(this);
    this.onRevert = this.onRevert.bind(this);
    this.isSelected = this.isSelected.bind(this);
    this.isEditing = this.isEditing.bind(this);
    this.isClearing = this.isClearing.bind(this);
    this.handleComponentKey = this.handleComponentKey.bind(this);

    this.handleKeyboardCellMovement = this.handleKeyboardCellMovement.bind(this);

    this.defaultState = {
      start: defaultLocation(),
      end: defaultLocation(),
      selecting: false,
      forceEdit: false,
      editing: defaultLocation(),
      clear: defaultLocation(),
    };
    this.state = this.defaultState;

    this.removeAllListeners = this.removeAllListeners.bind(this);
    this.handleIEClipboardEvents = this.handleIEClipboardEvents.bind(this);
  }

  removeAllListeners() {
    document.removeEventListener('mousedown', this.pageClick);
    document.removeEventListener('mouseup', this.onMouseUp);
    document.removeEventListener('cut', this.handleCut);
    document.removeEventListener('copy', this.handleCopy);
    document.removeEventListener('paste', this.handlePaste);
    document.removeEventListener('keydown', this.handleIEClipboardEvents);
  }

  componentDidMount() {
    // Add listener scoped to the DataSheet that catches otherwise unhandled
    // keyboard events when displaying components
    if (this.dgDom) {
      this.dgDom.addEventListener('keydown', this.handleComponentKey);
    }
  }

  componentWillUnmount() {
    if (this.dgDom) {
      this.dgDom.removeEventListener('keydown', this.handleComponentKey);
    }
    this.removeAllListeners();
  }

  isSelectionControlled() {
    return 'selected' in this.props;
  }

  getState() {
    let { state } = this;
    if (this.isSelectionControlled()) {
      let { start, end } = this.props.selected || {};
      start = start || this.defaultState.start;
      end = end || this.defaultState.end;
      state = { ...state, start, end };
    }
    return state;
  }

  // eslint-disable-next-line no-underscore-dangle,@typescript-eslint/no-explicit-any
  _setState(state: any) {
    const { editModeChanged } = this.props;
    if (editModeChanged && state.editing) {
      const wasEditing = !isEmpty(this.state.editing);
      const wilBeEditing = !isEmpty(state.editing);
      if (wasEditing !== wilBeEditing) {
        editModeChanged(wilBeEditing);
      }
    }
    if (this.isSelectionControlled() && ('start' in state || 'end' in state)) {
      // eslint-disable-next-line prefer-const
      let { start, end, ...rest } = state;
      let { selected } = this.props;
      const { onSelect } = this.props;
      selected = selected || { start: defaultLocation(), end: defaultLocation() };
      if (!start) {
        start = 'start' in selected ? selected.start : this.defaultState.start;
      }
      if (!end) {
        end = 'end' in selected ? selected.end : this.defaultState.end;
      }
      onSelect?.({ start, end });
      this.setState({ ...rest });
    } else {
      this.setState(state);
    }
  }

  pageClick(e: MouseEvent) {
    if (this.props.disablePageClick) return;
    const element = this.dgDom;
    if (!element.contains(e.target as Node)) {
      this.setState(this.defaultState);
      this.removeAllListeners();
    }
  }

  handleCut(e: ClipboardEvent | Event) {
    if (isEmpty(this.state.editing)) {
      e.preventDefault();
      this.handleCopy(e);
      const { start, end } = this.getState();
      this.clearSelectedCells(start, end);
    }
  }

  handleIEClipboardEvents(e: KeyboardEvent) {
    if (e.ctrlKey) {
      if (e.keyCode === 67) {
        // C - copy
        this.handleCopy(e);
      } else if (e.keyCode === 88) {
        // X - cut
        this.handleCut(e);
      } else if (e.keyCode === 86 || e.which === 86) {
        // P - patse
        this.handlePaste(e);
      }
    }
  }

  handleCopy(e: ClipboardEvent | Event) {
    if (isEmpty(this.state.editing)) {
      e.preventDefault();
      const { dataRenderer, valueRenderer, data } = this.props;
      const { start, end } = this.getState();

      if (this.props.handleCopy) {
        this.props.handleCopy({
          event: e,
          dataRenderer,
          valueRenderer,
          data,
          start,
          end,
          range,
        });
      } else {
        const text = range(start.i, end.i)
          .map((i) => range(start.j, end.j)
            .map((j) => {
              const cell = data[i][j];
              const value = dataRenderer ? dataRenderer(cell, i, j) : null;
              if (
                value === ''
                                    || value === null
                                    || typeof value === 'undefined'
              ) {
                return valueRenderer(cell, i, j);
              }
              return value;
            })
            .join('\t'))
          .join('\n');
        if ('clipboardData' in e) {
          e.clipboardData.setData('text/plain', text);
          e.preventDefault(); // ブラウザのデフォルト動作をキャンセル

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } else if ((window as any).clipboardData && (window as any).clipboardData.setData) {
          // 古い Internet Explorer 用の対応
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (window as any).clipboardData.setData('Text', text);
        }
      }
    }
  }

  handlePaste(e: ClipboardEvent | Event) {
    if (this.props.readonly === true) {
      return;
    }
    if (isEmpty(this.state.editing)) {
      let { start, end } = this.getState();

      start = { i: Math.min(start.i, end.i), j: Math.min(start.j, end.j) };
      end = { i: Math.max(start.i, end.i), j: Math.max(start.j, end.j) };

      const parse = this.props.parsePaste || defaultParsePaste;
      const changes: CellsChangedArgs<GridElement> = [];
      let pasteData: string[][] = [];

      if ('clipboardData' in e) {
        pasteData = parse(e.clipboardData.getData('text/plain'));
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } else if ((window as any).clipboardData && (window as any).clipboardData.getData) {
        // 古い Internet Explorer 用の対応
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        pasteData = parse((window as any).clipboardData.getData('Text'));
      }

      // in order of preference
      const { data, onCellsChanged /* , onPaste, onChange */ } = this.props;
      if (onCellsChanged) {
        const additions: CellAdditionsArgs = [];
        pasteData.forEach((row, i) => {
          row.forEach((value, j) => {
            end = { i: start.i + i, j: start.j + j };
            const cell = data[end.i] && data[end.i][end.j];
            if (!cell) {
              additions.push({ row: end.i, col: end.j, value });
            } else if (!cell.readOnly) {
              changes.push({ cell, row: end.i, col: end.j, value });
            }
          });
        });
        if (additions.length) {
          onCellsChanged(changes, additions);
        } else {
          onCellsChanged(changes);
        }
      }/* else if (onPaste) {
        pasteData.forEach((row, i) => {
          const rowData = [];
          row.forEach((pastedData, j) => {
            end = { i: start.i + i, j: start.j + j };
            const cell = data[end.i] && data[end.i][end.j];
            rowData.push({ cell, data: pastedData });
          });
          changes.push(rowData);
        });
        onPaste(changes);
      } else if (onChange) {
        pasteData.forEach((row, i) => {
          row.forEach((value, j) => {
            end = { i: start.i + i, j: start.j + j };
            const cell = data[end.i] && data[end.i][end.j];
            if (cell && !cell.readOnly) {
              onChange(cell, end.i, end.j, value);
            }
          });
        });
      }
      */
      // eslint-disable-next-line no-underscore-dangle
      this._setState({ end });
    }
  }

  handleKeyboardCellMovement(e: React.KeyboardEvent<HTMLElement>, commit: boolean = false) {
    const { start, editing } = this.getState();
    const { data } = this.props;
    const isEditing = editing && !isEmpty(editing);
    const currentCell = data[start.i] && data[start.i][start.j];

    if (isEditing && !commit) {
      return false;
    }
    const hasComponent = currentCell && currentCell.component;

    const keyCode = e.which || e.keyCode;

    if (hasComponent && isEditing) {
      e.preventDefault();
      return false;
    }

    if (keyCode === TAB_KEY) {
      this.handleNavigate(e, { i: 0, j: e.shiftKey ? -1 : 1 }, true);
    } else if (keyCode === RIGHT_KEY) {
      this.handleNavigate(e, { i: 0, j: 1 });
    } else if (keyCode === LEFT_KEY) {
      this.handleNavigate(e, { i: 0, j: -1 });
    } else if (keyCode === UP_KEY) {
      this.handleNavigate(e, { i: -1, j: 0 });
    } else if (keyCode === DOWN_KEY) {
      this.handleNavigate(e, { i: 1, j: 0 });
    } else if (commit && keyCode === ENTER_KEY) {
      this.handleNavigate(e, { i: e.shiftKey ? -1 : 1, j: 0 });
    }
    return false;
  }

  handleKey(e: React.KeyboardEvent<HTMLElement>) {
    if (e.isPropagationStopped && e.isPropagationStopped()) {
      return false;
    }
    const keyCode = e.which || e.keyCode;
    const { start, end, editing } = this.getState();
    const isEditing = editing && !isEmpty(editing);
    const noCellsSelected = !start || isEmpty(start);
    const ctrlKeyPressed = e.ctrlKey || e.metaKey;
    const deleteKeysPressed = keyCode === DELETE_KEY || keyCode === BACKSPACE_KEY;
    const enterKeyPressed = keyCode === ENTER_KEY;
    const numbersPressed = keyCode >= 48 && keyCode <= 57;
    const lettersPressed = keyCode >= 65 && keyCode <= 90;
    const latin1Supplement = keyCode >= 160 && keyCode <= 255;
    const numPadKeysPressed = keyCode >= 96 && keyCode <= 105;
    const currentCell = !noCellsSelected && this.props.data[start.i][start.j];
    const equationKeysPressed = [
      187 /* equal */, 189 /* substract */, 190 /* period */, 107 /* add */,
      109 /* decimal point */, 110,
    ].indexOf(keyCode) > -1;

    if (noCellsSelected || ctrlKeyPressed) {
      return true;
    }

    if (!isEditing) {
      this.handleKeyboardCellMovement(e);
      if (this.props.readonly === true) {
        return false;
      }
      if (deleteKeysPressed) {
        e.preventDefault();
        this.clearSelectedCells(start, end);
      } else if (currentCell && !currentCell.readOnly) {
        if (enterKeyPressed) {
          // eslint-disable-next-line no-underscore-dangle
          this._setState({ editing: start, clear: {}, forceEdit: true });
          e.preventDefault();
        } else if (
          numbersPressed
                    || numPadKeysPressed
                    || lettersPressed
                    || latin1Supplement
                    || equationKeysPressed
        ) {
          // empty out cell if user starts typing without pressing enter
          e.preventDefault();
          // eslint-disable-next-line no-underscore-dangle
          this._setState({ editing: start, clear: start, forceEdit: false });
        }
      }
    }
    return false;
  }

  getSelectedCells(data: GridElement[][], start: Location, end: Location) {
    const selected:CellsChangedArgs<GridElement, Value> = [];
    range(start.i, end.i).forEach((row) => {
      range(start.j, end.j).forEach((col) => {
        if (data[row] && data[row][col]) {
          const cell = data[row][col];
          selected.push({ cell, row, col, value: cell.value });
        }
      });
    });
    return selected;
  }

  clearSelectedCells(start: Location, end: Location) {
    const { data, onCellsChanged /* , onChange */ } = this.props;
    const cells = this.getSelectedCells(data, start, end)
      .filter((cell) => !cell.cell.readOnly)
      .map((cell) => ({ ...cell, value: '' }));
    if (onCellsChanged) {
      onCellsChanged(cells);
      this.onRevert();
    } /* else if (onChange) {
      // ugly solution brought to you by https://reactjs.org/docs/react-component.html#setstate
      // setState in a loop is unreliable
      setTimeout(() => {
        cells.forEach(({ cell, row, col, value }) => {
          onChange(cell, row, col, value);
        });
        this.onRevert();
      }, 0);
    } */
  }

  updateLocationSingleCell(location: Location) {
    // eslint-disable-next-line no-underscore-dangle
    this._setState({
      start: location,
      end: location,
      editing: {},
    });
  }

  updateLocationMultipleCells(offsets: Location) {
    const { start, end } = this.getState();
    const { data } = this.props;
    const oldStartLocation = { i: start.i, j: start.j };
    const newEndLocation = {
      i: end.i + offsets.i,
      j: Math.min(data[0].length - 1, Math.max(0, end.j + offsets.j)),
    };
    // eslint-disable-next-line no-underscore-dangle
    this._setState({
      start: oldStartLocation,
      end: newEndLocation,
      editing: {},
    });
  }

  searchForNextSelectablePos(isCellNavigable: (cell: GridElement, row: number, col: number, jumpNext: boolean) => boolean, data: GridElement[][], start: Location, offsets: Location, jumpRow: boolean) : Location | null {
    const previousRow = (location: Location) => ({
      i: location.i - 1,
      j: data[0].length - 1,
    });
    const nextRow = (location: Location) => ({ i: location.i + 1, j: 0 });
    const advanceOffset = (location: Location) => ({
      i: location.i + offsets.i,
      j: location.j + offsets.j,
    });
    const isCellDefined = ({ i, j }: Location) => data[i] && typeof data[i][j] !== 'undefined';

    let newLocation = advanceOffset(start);

    while (
      isCellDefined(newLocation)
            && !isCellNavigable(
              data[newLocation.i][newLocation.j],
              newLocation.i,
              newLocation.j,
              false,
            )
    ) {
      newLocation = advanceOffset(newLocation);
    }

    if (!isCellDefined(newLocation)) {
      if (!jumpRow) {
        return null;
      }
      if (offsets.j < 0) {
        newLocation = previousRow(newLocation);
      } else {
        newLocation = nextRow(newLocation);
      }
    }

    if (
      isCellDefined(newLocation)
            && !isCellNavigable(
              data[newLocation.i][newLocation.j],
              newLocation.i,
              newLocation.j,
              false,
            )
    ) {
      return this.searchForNextSelectablePos(
        isCellNavigable,
        data,
        newLocation,
        offsets,
        jumpRow,
      );
    } if (isCellDefined(newLocation)) {
      return newLocation;
    } 
    return null;
  }

  handleNavigate(e: React.KeyboardEvent<HTMLElement> | KeyboardEvent, offsets: Location, jumpRow: boolean = false) {
    if (offsets && (offsets.i || offsets.j)) {
      const { data } = this.props;
      const { start } = this.getState();

      const multiSelect = e.shiftKey && !jumpRow;
      const isCellNavigable = this.props.isCellNavigable
        ? this.props.isCellNavigable
        : () => true;

      if (multiSelect) {
        this.updateLocationMultipleCells(offsets);
      } else {
        const newLocation = this.searchForNextSelectablePos(
          isCellNavigable,
          data,
          start,
          offsets,
          jumpRow,
        );
        if (newLocation) {
          this.updateLocationSingleCell(newLocation);
        }
      }
      e.preventDefault();
    }
  }

  handleComponentKey(e: React.KeyboardEvent<HTMLElement> | KeyboardEvent) {
    // handles keyboard events when editing components
    const keyCode = e.which || e.keyCode;
    if (![ENTER_KEY, ESCAPE_KEY, TAB_KEY].includes(keyCode)) {
      return;
    }
    const { editing } = this.state;
    const { data } = this.props;
    const isEditing = !isEmpty(editing);
    if (isEditing) {
      const currentCell = data[editing.i][editing.j];
      const offset = e.shiftKey ? -1 : 1;
      if (currentCell && currentCell.component && !currentCell.forceComponent) {
        e.preventDefault();
        let func = this.onRevert; // ESCAPE_KEY
        if (keyCode === ENTER_KEY) {
          func = () => this.handleNavigate(e, { i: offset, j: 0 });
        } else if (keyCode === TAB_KEY) {
          func = () => this.handleNavigate(e, { i: 0, j: offset }, true);
        }
        // setTimeout makes sure that component is done handling the event before we take over
        setTimeout(() => {
          func();
          if (this.dgDom) {
            this.dgDom.focus({ preventScroll: true });
          }
        }, 1);
      }
    }
  }

  onContextMenu(evt: React.MouseEvent<HTMLElement>, i: number, j: number) {
    const cell = this.props.data[i][j];
    if (this.props.onContextMenu) {
      this.props.onContextMenu(evt, cell, i, j);
    }
  }

  onDoubleClick(i: number, j: number) {
    const cell = this.props.data[i][j];
    if (this.props.readonly !== true && !cell.readOnly) {
      // eslint-disable-next-line no-underscore-dangle
      this._setState({ editing: { i, j }, forceEdit: true, clear: {} });
    }
  }

  onMouseDown(i: number, j: number, e: React.MouseEvent<HTMLElement>) {
    const isNowEditingSameCell = !isEmpty(this.state.editing)
            && this.state.editing.i === i
            && this.state.editing.j === j;
    const editing = isEmpty(this.state.editing)
            || this.state.editing.i !== i
            || this.state.editing.j !== j
      ? {}
      : this.state.editing;

    // eslint-disable-next-line no-underscore-dangle
    this._setState({
      selecting: !isNowEditingSameCell,
      start: e.shiftKey ? this.getState().start : { i, j },
      end: { i, j },
      editing,
      forceEdit: !!isNowEditingSameCell,
    });

    const ua = window.navigator.userAgent;
    const isIE = /MSIE|Trident/.test(ua);
    // Listen for Ctrl + V in case of IE
    if (isIE) {
      document.addEventListener('keydown', this.handleIEClipboardEvents);
    }

    // Keep listening to mouse if user releases the mouse (dragging outside)
    document.addEventListener('mouseup', this.onMouseUp);
    // Listen for any outside mouse clicks
    document.addEventListener('mousedown', this.pageClick);

    // Cut, copy and paste event handlers
    document.addEventListener('cut', this.handleCut);
    document.addEventListener('copy', this.handleCopy);
    document.addEventListener('paste', this.handlePaste);
  }

  onMouseOver(i: number, j: number) {
    if (this.state.selecting && isEmpty(this.state.editing)) {
      // eslint-disable-next-line no-underscore-dangle
      this._setState({ end: { i, j } });
    }
  }

  onMouseUp() {
    // eslint-disable-next-line no-underscore-dangle
    this._setState({ selecting: false });
    document.removeEventListener('mouseup', this.onMouseUp);
  }

  onChange(row: number, col: number, value: Value) {
    const { /* onChange, */ onCellsChanged, data } = this.props;
    if (onCellsChanged) {
      onCellsChanged([{ cell: data[row][col], row, col, value }]);
    }/* else if (onChange) {
      onChange(data[row][col], row, col, value);
    } */
    this.onRevert();
  }

  onRevert() {
    // eslint-disable-next-line no-underscore-dangle
    this._setState({ editing: {} });
    // setTimeout makes sure that component is done handling the new state before we take over
    setTimeout(() => {
      if (this.dgDom) {
        this.dgDom.focus({ preventScroll: true });
      }
    }, 1);
  }

  componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<State>) {
    const { start, end } = this.state;
    const prevEnd = prevState.end;
    if (
      !isEmpty(end)
            && !(end.i === prevEnd.i && end.j === prevEnd.j)
            && !this.isSelectionControlled()
    ) {
      this.props.onSelect?.({ start, end });
    }
  }

  isSelectedRow(rowIndex: number) {
    const { start, end } = this.getState();
    const startY = start.i;
    const endY = end.i;
    if (startY <= endY) {
      return rowIndex >= startY && rowIndex <= endY;
    } 
    return rowIndex <= startY && rowIndex >= endY;
        
  }

  isSelected(i:number, j: number) {
    const { start, end } = this.getState();
    const posX = j >= start.j && j <= end.j;
    const negX = j <= start.j && j >= end.j;
    const posY = i >= start.i && i <= end.i;
    const negY = i <= start.i && i >= end.i;

    return (posX && posY) || (negX && posY) || (negX && negY) || (posX && negY);
  }

  isEditing(i: number, j: number) {
    return this.state.editing.i === i && this.state.editing.j === j;
  }

  isClearing(i: number, j: number) {
    return this.state.clear.i === i && this.state.clear.j === j;
  }

  render() {
    const {
      sheetRenderer: SheetRenderer = Sheet,
      rowRenderer: RowRenderer = Row,
      cellRenderer,
      dataRenderer,
      valueRenderer,
      dataEditor = CustomDataEditor,
      valueViewer = ValueViewer,
      attributesRenderer,
      className,
      overflow,
      data,
      keyFn,
    } = this.props;
    const { forceEdit } = this.state;
    return (
            <span
                ref={(r) => {
                  this.dgDom = r;
                }}
                tabIndex={0}
                className="data-grid-container"
                onKeyDown={this.handleKey}
            >
        <SheetRenderer
            data={data}
            className={['data-grid', className, overflow]
              .filter((a) => a)
              .join(' ')}
        >
          {data.map((row, i) => (
              <RowRenderer
                  key={keyFn ? keyFn(i) : i}
                  row={i}
                  cells={row}
                  selected={this.isSelectedRow(i)}
              >
                  {row.map((cell, j) => {
                    const isEditing = this.isEditing(i, j);
                    return (
                          <DataCell
                              key={cell.key ? cell.key : `${i}-${j}`}
                              row={i}
                              col={j}
                              cell={cell}
                              forceEdit={false}
                              onMouseDown={this.onMouseDown}
                              onMouseOver={this.onMouseOver}
                              onDoubleClick={this.onDoubleClick}
                              onContextMenu={this.onContextMenu}
                              onChange={this.onChange}
                              onRevert={this.onRevert}
                              onNavigate={this.handleKeyboardCellMovement}
                              onKeyUp={this.handleKey}
                              selected={this.isSelected(i, j)}
                              editing={isEditing}
                              clearing={this.isClearing(i, j)}
                              attributesRenderer={attributesRenderer}
                              cellRenderer={cellRenderer}
                              valueRenderer={valueRenderer}
                              dataRenderer={dataRenderer}
                              valueViewer={valueViewer}
                              dataEditor={dataEditor}
                              {...(isEditing
                                ? {
                                  forceEdit,
                                }
                                : {})}
                          />
                    );
                  })}
              </RowRenderer>
          ))}
        </SheetRenderer>
      </span>
    );
  }
}

